import type { ComponentChildren } from 'preact';

/**
 * Platform-standard sidebar + main two-column layout. One source of truth for the catalog's
 * filter sidebar, settings' section rail, admin's nav, and any future tool that needs a
 * persistent left rail.
 */
export interface TwoColumnShellProps {
  /** Sidebar content. Rendered inside an `<aside>` element. */
  sidebar: ComponentChildren;
  /** Main column content. Rendered inside `<section class="min-w-0 ...">`. */
  children: ComponentChildren;
  /**
   * Sidebar column width on the `lg` breakpoint and above.
   */
  sidebarWidth?: 240 | 260;
  /**
   * Sticky-aware sidebar treatment.
   */
  sticky?: boolean;
  /**
   * Visibility on mobile. `hidden` (default) hides the sidebar below `lg`; `block` keeps it
   * visible (stacks above the main column).
   */
  sidebarMobile?: 'hidden' | 'block';
  class?: string;
}

export function TwoColumnShell({
  sidebar,
  children,
  sidebarWidth = 240,
  sticky = true,
  sidebarMobile = 'hidden',
  class: extraClass,
}: TwoColumnShellProps) {
  // Tailwind v4 supports arbitrary template tokens like `lg:grid-cols-[240px_minmax(0,1fr)]`
  // but the JIT compiler needs each value present as a literal in the source - building the
  // class string dynamically won't emit. We branch on the two accepted widths instead.
  const gridClass =
    sidebarWidth === 260
      ? 'lg:grid-cols-[260px_minmax(0,1fr)]'
      : 'lg:grid-cols-[240px_minmax(0,1fr)]';

  const sidebarVisibility = sidebarMobile === 'hidden' ? 'hidden lg:block' : 'block';
  const sidebarSticky = sticky ? 'lg:sticky lg:top-[57px] lg:self-start lg:max-h-[calc(100vh-57px)] lg:overflow-y-auto' : '';

  return (
    <div class={`mx-auto grid min-h-[calc(100vh-57px)] max-w-[1680px] grid-cols-1 ${gridClass} ${extraClass ?? ''}`}>
      <aside class={`${sidebarVisibility} border-r border-rule ${sidebarSticky}`}>
        {sidebar}
      </aside>
      <section class="min-w-0 px-[clamp(20px,3.5vw,56px)] pt-8 pb-16">
        {children}
      </section>
    </div>
  );
}
